/** How long the UI waits for triggered step-test runs before giving up. */
export const STEP_TEST_TIMEOUT_MS = 120_000;

export type StepFailure = {
  stepDir: string;
  timedOut: boolean;
  // Empty when the run errored before producing any spec results (or timed
  // out); otherwise the specs that failed.
  specs: { name: string; message: string | null }[];
};

export type TestExecution = {
  id: string;
  status: string;
  testSpecs: readonly {
    name: string;
    status: string;
    message: string | null;
  }[];
};

/**
 * Decide which triggered step-test runs count as failures. A run fails if any
 * spec failed, if the execution itself errored, or — once we've timed out
 * waiting — if it never finished.
 */
export function computeStepTestFailures(
  runs: { stepDir: string; executionId: string }[],
  byId: Map<string, TestExecution>,
  timedOut: boolean
): StepFailure[] {
  const out: StepFailure[] = [];
  for (const run of runs) {
    const exec = byId.get(run.executionId);
    if (!exec || exec.status === "pending") {
      if (timedOut)
        out.push({ stepDir: run.stepDir, timedOut: true, specs: [] });
      continue;
    }
    const specs = exec.testSpecs
      .filter((s) => s.status === "failed")
      .map((s) => ({ name: s.name, message: s.message }));
    if (specs.length > 0) {
      out.push({ stepDir: run.stepDir, timedOut: false, specs });
    } else if (exec.status === "error") {
      out.push({ stepDir: run.stepDir, timedOut: false, specs: [] });
    }
  }
  return out;
}
